import React, { useEffect, useState } from 'react';
import { DrawerComponent } from 'components/Drawer';
import CreditCard from 'components/Icons/CreditCard';
import { api } from 'services/api';
import { AdicionarCartaoDrawer } from './AdicionarCartaoDrawer';

type PaymentMethod = {
  id: number;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
  stripe_payment_method_id: string;
};

type PendingCard = {
  token: string;
  brand: string;
  last4: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string | null;
  idToken: string | null;
};

const STEP = 1000;

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const TrashIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334Z'
      stroke='#298EE6'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export const AutoRecargaModal: React.FC<Props> = ({
  isOpen,
  onClose,
  accessToken,
  idToken,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [originalPaymentMethodId, setOriginalPaymentMethodId] = useState<number | null>(null);
  const [pendingCard, setPendingCard] = useState<PendingCard | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [triggerBalance, setTriggerBalance] = useState(1000);
  const [koinQuantity, setKoinQuantity] = useState(4000);
  const [basePriceBrl, setBasePriceBrl] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headers = { Authorization: `Bearer ${accessToken}`, idToken };
  const minKoinQuantity = Math.max(4000, triggerBalance * 2);
  const packagePrice = koinQuantity * basePriceBrl;

  // card to display: pending (not yet saved) takes precedence over loaded card
  const displayedCard = pendingCard
    ? { brand: pendingCard.brand, last4: pendingCard.last4 }
    : paymentMethod
    ? { brand: paymentMethod.brand, last4: paymentMethod.last4 }
    : null;

  useEffect(() => {
    setKoinQuantity(v => Math.max(minKoinQuantity, v));
  }, [minKoinQuantity]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      try {
        const [pmRes, configRes] = await Promise.all([
          api.get('lya/payment-methods', { headers }),
          api.get('lya/auto-recharge', { headers }),
        ]);

        setBasePriceBrl(configRes.data.data.base_price_brl ?? 0);

        const pms: PaymentMethod[] = pmRes.data.data;
        const pm = pms.find(p => p.is_default) ?? pms[0] ?? null;
        setPaymentMethod(pm);
        setOriginalPaymentMethodId(pm?.id ?? null);
        setPendingCard(null);

        const config = configRes.data.data;
        if (config) {
          setIsEnabled(config.enabled ?? false);
          const tb = Math.max(1000, config.trigger_balance ?? 1000);
          setTriggerBalance(tb);
          setKoinQuantity(Math.max(4000, tb * 2, config.koin_quantity ?? 0));
        }
      } catch {
        setError('Erro ao carregar configurações.');
      }
    };
    fetchData();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCardAdded = (token: string, brand: string, last4: string) => {
    setPendingCard({ token, brand, last4 });
    setPaymentMethod(null);
  };

  const handleRemoveCard = async () => {
    if (!isEnabled && paymentMethod) {
      try {
        await api.delete(`lya/payment-methods/${paymentMethod.id}`, { headers });
      } catch {
        setError('Erro ao remover cartão.');
        return;
      }
    }
    setPaymentMethod(null);
    setPendingCard(null);
  };

  const handleSave = async (enabled: boolean) => {
    enabled ? setLoading(true) : setDeactivating(true);
    setError(null);
    try {
      // 1. Delete original card if it was removed or replaced
      if (originalPaymentMethodId !== null && (pendingCard !== null || paymentMethod === null)) {
        await api.delete(`lya/payment-methods/${originalPaymentMethodId}`, { headers });
      }

      // 2. Create new card if pending, then fetch to get stripe_payment_method_id
      let stripePaymentMethodId: string | null = paymentMethod?.stripe_payment_method_id ?? null;
      if (pendingCard) {
        await api.post(
          'lya/payment-methods/setup',
          { card_token: pendingCard.token, provider: 'pagarme' },
          { headers: { Authorization: `Bearer ${accessToken}`, idToken } },
        );
        const pmRes = await api.get('lya/payment-methods', { headers });
        const pms: PaymentMethod[] = pmRes.data.data;
        const newPm = pms.find(p => p.is_default) ?? pms[0] ?? null;
        stripePaymentMethodId = newPm?.stripe_payment_method_id ?? null;
      }

      // 3. Save auto-recharge
      await api.post(
        'lya/auto-recharge',
        {
          enabled,
          trigger_balance: triggerBalance,
          koin_quantity: koinQuantity,
          stripe_payment_method_id: stripePaymentMethodId,
        },
        { headers: { Authorization: `Bearer ${accessToken}`, idToken } },
      );
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
      setDeactivating(false);
    }
  };

  return (
    <>
      <DrawerComponent
        open={isOpen}
        size='default'
        onCompleteClose={onClose}
        noHeader
        className='drawer-full-height'
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            margin: '0 -2rem',
          }}
        >
          {/* ── Header fixo ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 2rem 16px',
              borderBottom: '1px solid #e0e0e0',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px', fontWeight: 700 }}>
                Recarga automática
              </span>
              {isEnabled && (
                <span
                  style={{
                    background: '#D1FAE5',
                    color: '#065F46',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: '20px',
                    padding: '2px 10px',
                  }}
                >
                  Ativo
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#555',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* ── Área scrollável ── */}
          <div style={{ overflowY: 'auto', padding: '24px 2rem 0', height: '100%' }}>
            {error && (
              <p style={{ color: '#c0392b', fontSize: '13px', marginBottom: '12px' }}>
                {error}
              </p>
            )}

            {/* Trigger balance stepper */}
            <label style={{ fontSize: '14px', color: '#1a1a1a', marginBottom: '8px', display: 'block' }}>
              Quando o saldo ficar abaixo de
            </label>
            <div style={stepperContainerStyle}>
              <button
                style={stepperBtnStyle}
                onClick={() => setTriggerBalance(v => Math.max(1000, v - STEP))}
                disabled={triggerBalance <= 1000}
              >
                —
              </button>
              <span style={{ fontSize: '15px', fontWeight: 500 }}>
                {triggerBalance.toLocaleString('pt-BR')} koins
              </span>
              <button
                style={stepperBtnStyle}
                onClick={() => setTriggerBalance(v => v + STEP)}
              >
                +
              </button>
            </div>

            {/* Koin quantity stepper */}
            <label style={{ fontSize: '14px', color: '#1a1a1a', margin: '20px 0 8px', display: 'block' }}>
              Adicionar um pacote de
            </label>
            <div style={stepperContainerStyle}>
              <button
                style={stepperBtnStyle}
                onClick={() => setKoinQuantity(v => Math.max(minKoinQuantity, v - STEP))}
                disabled={koinQuantity <= minKoinQuantity}
              >
                —
              </button>
              <span style={{ fontSize: '15px', fontWeight: 500 }}>
                {koinQuantity.toLocaleString('pt-BR')} koins
              </span>
              <button
                style={stepperBtnStyle}
                onClick={() => setKoinQuantity(v => v + STEP)}
              >
                +
              </button>
            </div>

            {/* Payment method */}
            <p style={{ fontSize: '14px', fontWeight: 600, margin: '24px 0 12px' }}>
              Forma de pagamento
            </p>
            {displayedCard ? (
              <div
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CreditCard />
                  <div>
                    <div style={{ fontSize: '12px', color: '#808080' }}>Cartão de crédito</div>
                    <div style={{ fontSize: '14px', fontWeight: 500 }}>
                      {displayedCard.brand} •••• {displayedCard.last4}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleRemoveCard}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#298EE6',
                    fontWeight: 600,
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <TrashIcon /> Remover
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAddCardOpen(true)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#fff',
                  color: '#298EE6',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                + Adicionar cartão
              </button>
            )}
          </div>

          {/* ── Footer fixo ── */}
          <div
            style={{
              borderTop: '1px solid #e0e0e0',
              padding: '16px 2rem 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '16px',
              flexShrink: 0,
              background: '#fff',
            }}
          >
            {isEnabled && (
              <button
                onClick={() => handleSave(false)}
                disabled={deactivating}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: deactivating ? 'not-allowed' : 'pointer',
                  color: '#298EE6',
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                {deactivating ? 'Desativando...' : 'Desativar'}
              </button>
            )}
            <div style={{ flex: 1, textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#808080' }}>Valor por pacote</div>
              <div style={{ fontSize: '15px', fontWeight: 700 }}>
                {basePriceBrl > 0 ? formatBRL(packagePrice) : '—'}
              </div>
            </div>
            <button
              onClick={() => handleSave(!!displayedCard)}
              disabled={loading || !displayedCard}
              style={{
                padding: '12px 20px',
                background: loading || !displayedCard ? '#ccc' : '#298EE6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: loading || !displayedCard ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                fontSize: '14px',
                whiteSpace: 'nowrap',
              }}
            >
              {loading ? 'Salvando...' : isEnabled ? 'Atualizar' : 'Ativar'}
            </button>
          </div>
        </div>
      </DrawerComponent>

      {isAddCardOpen && (
        <AdicionarCartaoDrawer
          isOpen={isAddCardOpen}
          onClose={() => setIsAddCardOpen(false)}
          onSuccess={handleCardAdded}
          accessToken={accessToken}
          idToken={idToken}
        />
      )}
    </>
  );
};

const stepperContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  padding: '12px 16px',
};

const stepperBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '18px',
  fontWeight: 700,
  color: '#1a1a1a',
  lineHeight: 1,
  padding: '0 4px',
};
